import type { IUserProfile } from '@/context/auth/types';
import type {
  AdminCreateIdentityRequest,
  AdminCreateOrganizationMembershipRequest,
  AdminDisableOrganizationMembershipAcrossAllIdentitiesRequest,
  AdminDisableOrganizationMembershipRequest,
  AdminEnableOrganizationMembershipAcrossAllIdentitiesRequest,
  AdminEnableOrganizationMembershipRequest,
  AdminGetIdentityDetailsRequest,
  AdminResendInvitationEmailRequest,
  AdminSearchOrganizationMembershipsRequest,
  AdminUpdateIdentityRequest,
  IdentityDetails,
  PersistedIdentityDetails,
  PersistedOrganizationMembershipDetails,
} from '@xpanse/authz-service-client';

import {
  useIdentityManagementApi,
  useOrganizationMembershipManagementApi,
  useUsersApi,
} from '@/api/auth-z';
import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useXpanseAuth } from '@/hooks/useXpanseAuthentication';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const queryKey = (searchUsersRequest: AdminSearchOrganizationMembershipsRequest) => [
  'users',
  searchUsersRequest,
];

export const useUserProfile = (): IUserProfile => {
  const { user } = useXpanseAuth();

  return user?.['https://xpanse.com/claims/auth/context'] ?? {};
};

export const useSearchUsers = (searchUsersRequest: AdminSearchOrganizationMembershipsRequest) => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  return useQuery({
    queryKey: queryKey(searchUsersRequest),
    queryFn: () => orgMembersApi.adminSearchOrganizationMemberships(searchUsersRequest),
  });
};

export const useSendUserEmail = () => {
  const identityManagementApi = useIdentityManagementApi();
  const { reportError } = useErrorNotification();
  return useMutation<void, Error, AdminResendInvitationEmailRequest>({
    mutationFn: ({ identityCode }) =>
      identityManagementApi.adminResendInvitationEmail({ identityCode }),
    onError: (error) => {
      console.error('Error in [adminResendInvitationEmail] mutation: ', error);
      reportError(error);
    },
  });
};

export const useDisableUser = () => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();
  return useMutation<
    PersistedOrganizationMembershipDetails,
    Error,
    AdminDisableOrganizationMembershipRequest
  >({
    mutationFn: ({ organizationCode, identityCode }) =>
      orgMembersApi.adminDisableOrganizationMembership({
        identityCode,
        organizationCode,
      }),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: queryKey({ organizationCode: variables.organizationCode }),
      }),
    onError: (error) => {
      console.error('Error in [adminDisableOrganizationMembership] mutation: ', error);
      reportError(error);
    },
  });
};

export const useDisableAllUsers = () => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();
  return useMutation<
    PersistedOrganizationMembershipDetails[],
    Error,
    AdminDisableOrganizationMembershipAcrossAllIdentitiesRequest
  >({
    mutationFn: ({ organizationCode }) =>
      orgMembersApi.adminDisableOrganizationMembershipAcrossAllIdentities({ organizationCode }),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: queryKey({ organizationCode: variables.organizationCode }),
      }),
    onError: (error) => {
      console.error(
        'Error in [adminDisableOrganizationMembershipAcrossAllIdentities] mutation: ',
        error
      );
      reportError(error);
    },
  });
};

export const useEnableUser = () => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();
  return useMutation<
    PersistedOrganizationMembershipDetails,
    Error,
    AdminEnableOrganizationMembershipRequest
  >({
    mutationFn: ({ organizationCode, identityCode }) =>
      orgMembersApi.adminEnableOrganizationMembership({
        identityCode,
        organizationCode,
      }),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: queryKey({ organizationCode: variables.organizationCode }),
      }),
    onError: (error) => {
      console.error('Error in [adminEnableOrganizationMembership] mutation: ', error);
      reportError(error);
    },
  });
};

export const useEnableAllUsers = () => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();
  return useMutation<
    PersistedOrganizationMembershipDetails[],
    Error,
    AdminEnableOrganizationMembershipAcrossAllIdentitiesRequest
  >({
    mutationFn: ({ organizationCode }) =>
      orgMembersApi.adminDisableOrganizationMembershipAcrossAllIdentities({ organizationCode }),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: queryKey({ organizationCode: variables.organizationCode }),
      }),
    onError: (error) => {
      console.error(
        'Error in [adminEnableOrganizationMembershipAcrossAllIdentities] mutation: ',
        error
      );
      reportError(error);
    },
  });
};

export const useGetAuthenticationContexts = () => {
  const usersApi = useUsersApi();
  return useQuery({
    queryKey: ['auth-contexts'],
    queryFn: () => usersApi.getAuthenticationContexts(),
  });
};

export const useGetUserContexts = () => {
  const usersApi = useUsersApi();

  return useQuery({
    queryKey: ['user-contexts'],
    queryFn: () => usersApi.getUserContexts(),
  });
};

const useCreateIdentity = () => {
  const identityManagementApi = useIdentityManagementApi();
  const { reportError } = useErrorNotification();
  return useMutation<PersistedIdentityDetails, Error, AdminCreateIdentityRequest>({
    mutationFn: ({ identityDetails, sendInvitationEmail }) =>
      identityManagementApi.adminCreateIdentity({ identityDetails, sendInvitationEmail }),
    onError: (error) => {
      console.error('Error in [adminCreateIdentity] mutation: ', error);
      reportError(error);
    },
  });
};

const useMutateOrgMembership = () => {
  const orgMembersApi = useOrganizationMembershipManagementApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();
  return useMutation<
    PersistedOrganizationMembershipDetails,
    Error,
    AdminCreateOrganizationMembershipRequest
  >({
    mutationFn: (user) => orgMembersApi.adminCreateOrganizationMembership(user),
    onSuccess: ({ organizationCode }) =>
      queryClient.invalidateQueries({ queryKey: queryKey({ organizationCode }) }),
    onError: (error) => {
      console.error('Error in [adminCreateOrganizationMembership] mutation: ', error);
      reportError(error);
    },
  });
};

interface ICreateUserValues extends IdentityDetails {
  roles: string[];
}

export const useCreateUser = () => {
  const { isPending: isCreatingIdentity, mutateAsync: createIdentity } = useCreateIdentity();
  const { isPending: isCreatingOrgMembership, mutate: createOrgMembership } =
    useMutateOrgMembership();

  return {
    isCreatingUser: isCreatingIdentity || isCreatingOrgMembership,
    createUser: async ({
      identityDetails,
      organizationCode,
      sendInvitationEmail,
    }: {
      identityDetails: ICreateUserValues;
      organizationCode: string;
      sendInvitationEmail: boolean;
    }) => {
      const {
        active,
        email,
        firstName,
        lastName,
        phone,
        alternateEmail,
        meridianLinkUserId,
        roles,
      } = identityDetails;
      const identity = await createIdentity({
        identityDetails: {
          active,
          email,
          firstName,
          lastName,
          phone,
          alternateEmail,
          meridianLinkUserId,
        },
        sendInvitationEmail,
      });
      return createOrgMembership({
        identityCode: identity.code,
        identityMembershipDetails: { active, roles: new Set(roles) },
        organizationCode,
      });
    },
  };
};

export const useCreateUsers = () => {
  const { createUser, isCreatingUser } = useCreateUser();

  const createUsers = async ({
    identities,
    organizationCode,
    sendInvitationEmail,
  }: {
    identities: ICreateUserValues[];
    organizationCode: string;
    sendInvitationEmail: boolean;
  }) =>
    Promise.all(
      identities.map(async (identityDetails) => {
        try {
          await createUser({ identityDetails, organizationCode, sendInvitationEmail });
        } catch (error) {
          throw new Error(error as string);
        }
      })
    );

  return {
    isCreatingUsers: isCreatingUser,
    createUsers,
  };
};

export const useUpdateIdentity = () => {
  const identityManagementApi = useIdentityManagementApi();

  return useMutation<PersistedIdentityDetails, Error, AdminUpdateIdentityRequest>({
    mutationFn: ({ identityCode, identityDetails }) =>
      identityManagementApi.adminUpdateIdentity({ identityCode, identityDetails }),
  });
};
export const useGetIdentity = () => {
  const identityManagementApi = useIdentityManagementApi();

  return useMutation<PersistedIdentityDetails, Error, AdminGetIdentityDetailsRequest>({
    mutationFn: ({ identityCode }) =>
      identityManagementApi.adminGetIdentityDetails({ identityCode }),
  });
};
